import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { FaBirthdayCake, FaCalendarAlt, FaEnvelope, FaPhone, FaSearch, FaShieldAlt } from 'react-icons/fa'
import { Report } from 'notiflix/build/notiflix-report-aio'
import api from '../../../helpers/apiRequester'
import styles from './styles.module.scss'

interface AuditClaim {
    case_reference: string | null
    lender: string | null
    policy_number: string | null
    ate_policy_number: string | null
    ate_inception_date: string | null
}

interface AuditClient {
    client_id: number
    name: string
    email_address: string | null
    phone_number: string | null
    ate_policy_number: string | null
    ate_inception_date: string | null
    dob: string | null
    claim_count: number
    total_value: number | null
    case_references: string | null
    introducers: string | null
    claims: AuditClaim[]
}

const formatCurrency = (value: number | null) => {
    if (value === null || value === undefined) return '—'
    return `£${Number(value).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

const formatShortDate = (value: string | null) => {
    if (!value) return null
    try {
        const date = new Date(value)
        if (isNaN(date.getTime())) return null
        return date.toLocaleDateString('en-GB', { year: 'numeric', month: 'short', day: 'numeric' })
    } catch {
        return value
    }
}

export const InsuranceAuditView = () => {
    const navigate = useNavigate()
    const [clients, setClients] = useState<AuditClient[]>([])
    const [searchResults, setSearchResults] = useState<AuditClient[] | null>(null)
    const [searchTerm, setSearchTerm] = useState('')
    const [loading, setLoading] = useState(true)
    const [searching, setSearching] = useState(false)
    const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

    useEffect(() => {
        loadClients()
    }, [])

    const loadClients = async () => {
        try {
            setLoading(true)
            const result = await api({ url: '/insurance-audit-clients', data: {} })
            if (result.data.success) {
                setClients(result.data.clients || [])
            } else {
                Report.failure('Error', 'Failed to load audit cases.', 'Okay')
            }
        } catch (error) {
            console.error('Error loading audit clients:', error)
            Report.failure('Error', 'Failed to load audit cases.', 'Okay')
        } finally {
            setLoading(false)
        }
    }

    // Debounced server-side search (email / name / case reference / claimant
    // ID), restricted server-side to the whitelisted case set.
    const handleSearchChange = (value: string) => {
        setSearchTerm(value)
        if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current)

        const term = value.trim()
        if (term.length < 3) {
            setSearchResults(null)
            setSearching(false)
            return
        }

        setSearching(true)
        searchDebounceRef.current = setTimeout(async () => {
            try {
                const result = await api({ url: '/insurance-audit-search', data: { searchTerm: term } })
                setSearchResults(result.data.success ? result.data.clients || [] : [])
            } catch (error) {
                console.error('Error searching audit clients:', error)
                setSearchResults([])
            } finally {
                setSearching(false)
            }
        }, 350)
    }

    const displayedClients = searchResults !== null ? searchResults : clients
    const totalValue = displayedClients.reduce((sum, client) => sum + Number(client.total_value || 0), 0)

    return (
        <div className={styles.auditView}>
            <div className={styles.toolbar}>
                <div className={styles.searchBox}>
                    <FaSearch className={styles.searchIcon} />
                    <input
                        type="text"
                        placeholder="Search by name, email, case reference or claimant ID..."
                        value={searchTerm}
                        onChange={(e) => handleSearchChange(e.target.value)}
                    />
                </div>
                <div className={styles.summaryStats}>
                    <div className={styles.stat}>
                        <span className={styles.statNumber}>{displayedClients.length}</span>
                        <span className={styles.statLabel}>Clients</span>
                    </div>
                    <div className={styles.stat}>
                        <span className={styles.statNumber}>
                            {displayedClients.reduce((sum, client) => sum + Number(client.claim_count || 0), 0)}
                        </span>
                        <span className={styles.statLabel}>Claims</span>
                    </div>
                    <div className={styles.stat}>
                        <span className={styles.statNumber}>{formatCurrency(totalValue)}</span>
                        <span className={styles.statLabel}>Total Value</span>
                    </div>
                </div>
            </div>

            {loading ? (
                <div className={styles.emptyState}>Loading audit cases...</div>
            ) : searching ? (
                <div className={styles.emptyState}>Searching...</div>
            ) : displayedClients.length === 0 ? (
                <div className={styles.emptyState}>
                    {searchResults !== null
                        ? 'No clients match your search.'
                        : 'No audit cases are available to you yet. Please contact Sentinel if you were expecting access.'}
                </div>
            ) : (
                <table className={styles.table}>
                    <thead>
                        <tr>
                            <th>Claimant ID</th>
                            <th>Client</th>
                            <th>Contact</th>
                            <th className={styles.centered}>Cases</th>
                            <th>Insurance</th>
                        </tr>
                    </thead>
                    <tbody>
                        {displayedClients.map((client) => (
                            <tr
                                key={client.client_id}
                                className={styles.clickableRow}
                                onClick={() => navigate(`/insurance-audit/client/${client.client_id}`)}
                            >
                                <td className={styles.claimantId}>{client.client_id}</td>
                                <td>
                                    <div className={styles.clientCell}>
                                        <span className={styles.clientName}>{client.name || '—'}</span>
                                        {formatShortDate(client.dob) && (
                                            <span className={styles.subDetail}>
                                                <FaBirthdayCake /> {formatShortDate(client.dob)}
                                            </span>
                                        )}
                                    </div>
                                </td>
                                <td className={styles.emailCell}>
                                    <div className={styles.contactCell}>
                                        {client.email_address ? (
                                            <span className={styles.emailChip} title={client.email_address}>
                                                <FaEnvelope /> {client.email_address}
                                            </span>
                                        ) : (
                                            '—'
                                        )}
                                        {client.phone_number && (
                                            <span className={styles.phoneChip} title={client.phone_number}>
                                                <FaPhone /> {client.phone_number}
                                            </span>
                                        )}
                                    </div>
                                </td>
                                <td className={styles.centered}>
                                    <div className={styles.casesWrapper}>
                                        <span className={styles.casesBadge}>{client.claim_count}</span>
                                        {client.claims && client.claims.length > 0 && (
                                            <div className={styles.casesTooltip}>
                                                <div className={styles.tooltipTitle}>
                                                    {client.claims.length} Case
                                                    {client.claims.length === 1 ? '' : 's'}
                                                </div>
                                                {client.claims.map((claim, index) => (
                                                    <div
                                                        key={`${claim.case_reference}-${index}`}
                                                        className={styles.tooltipClaim}
                                                    >
                                                        <span className={styles.tooltipCaseRef}>
                                                            {claim.case_reference || '—'}
                                                        </span>
                                                        <span className={styles.tooltipRow}>
                                                            <span className={styles.tooltipLabel}>Lender</span>
                                                            <span>{claim.lender || '—'}</span>
                                                        </span>
                                                        <span className={styles.tooltipRow}>
                                                            <span className={styles.tooltipLabel}>Policy No.</span>
                                                            <span>{claim.policy_number || '—'}</span>
                                                        </span>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </td>
                                <td>
                                    <div className={styles.ateCell}>
                                        {client.ate_policy_number ? (
                                            <span className={styles.atePolicy}>
                                                <FaShieldAlt /> {client.ate_policy_number}
                                            </span>
                                        ) : (
                                            '—'
                                        )}
                                        {formatShortDate(client.ate_inception_date) && (
                                            <span className={styles.subDetail}>
                                                <FaCalendarAlt /> {formatShortDate(client.ate_inception_date)}
                                            </span>
                                        )}
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
        </div>
    )
}
